import { viewerApi } from './viewerApi'

// ---------------------------------------------------------------------------
// loadSampleIfc: fetch a public, CORS-enabled sample IFC and feed it to the
// viewer's loader as if the user had picked it from disk. Lets anyone without
// an IFC at hand try the viewer (especially on a phone, where there's often
// no file to open).
//
// The models are served from raw.githubusercontent.com, which sends
// Access-Control-Allow-Origin: *, so the cross-origin fetch is allowed and
// the bytes go straight into web-ifc. Download progress is rebroadcast on the
// same 'vn2r:viewer-stage' window event the ribbon's LoadingPill listens on.
// ---------------------------------------------------------------------------

export interface SampleIfc {
  id: string
  label: string
  filename: string
  url: string
  sizeLabel: string
  description: string
}

export const SAMPLE_IFCS: SampleIfc[] = [
  {
    id: 'house',
    label: 'Casa (exemplo)',
    filename: 'exemplo-casa.ifc',
    url: 'https://raw.githubusercontent.com/ThatOpen/engine_web-ifc/main/examples/example.ifc',
    sizeLabel: '~0,4 MB',
    description:
      'Modelo pequeno (IFC2x3) com paredes, lajes e dois pisos. Carrega em segundos, ideal para testar em rede móvel.',
  },
  {
    id: 'school',
    label: 'Escola (estrutura)',
    filename: 'exemplo-escola-estrutura.ifc',
    url: 'https://raw.githubusercontent.com/ThatOpen/engine_components/main/resources/ifc/school_str.ifc',
    sizeLabel: '~8,6 MB',
    description:
      'Modelo estrutural maior. Demora mais a descarregar, sobretudo em rede móvel.',
  },
  {
    id: 'clinic',
    label: 'Clínica (arquitetura)',
    filename: 'exemplo-clinica.ifc',
    url: 'https://raw.githubusercontent.com/buildingSMART/Sample-Test-Files/main/IFC%204.0.2.1%20(IFC%204)/PCERT-Sample-Scene/Building-Architecture.ifc',
    sizeLabel: '~0,2 MB',
    description:
      'Modelo de arquitetura (IFC4, buildingSMART) da cena de certificação PCERT. Pequeno e rápido, bom para demonstrar a validação IDS.',
  },
]

export const DEFAULT_SAMPLE = SAMPLE_IFCS[0]

function broadcastStage(loading: boolean, stage: string | null) {
  window.dispatchEvent(
    new CustomEvent('vn2r:viewer-stage', { detail: { loading, stage } }),
  )
}

// Guard against a double-tap kicking off two concurrent downloads.
let inFlight = false

export async function loadSampleIfc(
  sample: SampleIfc = DEFAULT_SAMPLE,
  options?: { replace?: boolean },
): Promise<void> {
  if (inFlight) return
  if (!viewerApi.hasIfcLoader()) {
    throw new Error('Viewer ainda não está pronto.')
  }
  inFlight = true
  try {
    broadcastStage(true, `A descarregar exemplo (${sample.sizeLabel})`)
    let res: Response
    try {
      res = await fetch(sample.url, { mode: 'cors' })
    } catch {
      throw new Error(
        'Não foi possível descarregar o exemplo. Verifica a ligação à internet.',
      )
    }
    if (!res.ok) {
      throw new Error(`Falha a descarregar o exemplo (HTTP ${res.status}).`)
    }

    // Stream with a readout of how much has arrived. We show megabytes
    // received rather than a percentage: the host (GitHub) gzips the
    // response, so content-length is the COMPRESSED size while the reader
    // hands us decompressed bytes, so a percentage would shoot past 100%.
    let bytes: Uint8Array
    if (res.body) {
      const reader = res.body.getReader()
      const chunks: Uint8Array[] = []
      let received = 0
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        if (value) {
          chunks.push(value)
          received += value.length
          const mb = (received / 1e6).toLocaleString('pt-PT', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })
          broadcastStage(true, `A descarregar exemplo ${mb} MB`)
        }
      }
      bytes = new Uint8Array(received)
      let offset = 0
      for (const c of chunks) {
        bytes.set(c, offset)
        offset += c.length
      }
    } else {
      bytes = new Uint8Array(await res.arrayBuffer())
    }

    // Copy into a plain ArrayBuffer (the page is cross-origin isolated, so a
    // raw Uint8Array may be SharedArrayBuffer-backed, which BlobPart rejects).
    const ab = bytes.buffer.slice(
      bytes.byteOffset,
      bytes.byteOffset + bytes.byteLength,
    ) as ArrayBuffer
    const file = new File([ab], sample.filename, {
      type: 'application/octet-stream',
    })
    // Hand off to the real loader, which drives its own staged progress.
    broadcastStage(false, null)
    const aberto = await viewerApi.loadIfcFile(file, {
      replace: options?.replace ?? true,
    })
    // O carregador não rejeita: devolve o resultado. Quem chama esta
    // função espera uma excepção quando falha (DeepLinkLoader, botão
    // "Abrir exemplo"), por isso a falha passa a excepção aqui.
    if (!aberto.ok) throw new Error(aberto.erro)
  } catch (err) {
    broadcastStage(false, null)
    throw err
  } finally {
    inFlight = false
  }
}
